import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import PMBSelect, { PMBSelectedSummary } from 'components/PMBSelect'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import { useFetchWarehouseNetworkItemsQuery } from 'features/private-views/inventori-intelligent/inventoriIntelligentApiSlice'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
} from 'react-feather'
import Flatpickr from 'react-flatpickr'
import { useSelector } from 'react-redux'
import {
  Alert,
  Badge,
  Card,
  CardBody,
  Col,
  Collapse,
  Row,
} from 'reactstrap'
import ComparisonChart from './ComparisonChart'
import SummaryCards from './SummaryCards'
import SummaryTable from './SummaryTable'
import { buildFilteredYearSummary, filterProductRows, hasTransactionValue, summarizeProductRows } from './summaryUtils'
import {
  useLazyFetchTransactionSummaryYearQuery,
  useLazyFetchSummaryProductsQuery,
  useFetchSummaryProductsQuery,
  useLazyFetchSummaryCounterpartsQuery,
} from './transactionSummaryApiSlice'

const TransactionSummary = () => {
  const { profile } = useSelector((s) => s.dashboard)

  const [activeTab, setActiveTab] = useState('tahunan')
  const [selectedYearAnnual, setSelectedYearAnnual] = useState(null)
  const [selectedMonthYear, setSelectedMonthYear] = useState(
    new Date().toISOString(),
  )
  const [counterpart, setCounterpart] = useState(undefined)
  const [itemsFilter, setItemsFilter] = useState([])
  const [isShowFilter, setIsShowFilter] = useState(false)
  const [yearOptionsAnnual, setYearOptionsAnnual] = useState([])

  // API hooks
  const [fetchSummaryYear, { data: summaryYearData, isLoading: isSummaryYearLoading, isError: isSummaryYearError }] =
    useLazyFetchTransactionSummaryYearQuery()
  const [fetchCounterparts, { data: counterpartsData }] =
    useLazyFetchSummaryCounterpartsQuery()
  const { data: itemsData } = useFetchWarehouseNetworkItemsQuery({ page: 0, size: 999 })
  const [fetchYearMonthProducts] =
    useLazyFetchSummaryProductsQuery()
  const [yearMonthRows, setYearMonthRows] = useState(null)
  const [isYearFilterLoading, setIsYearFilterLoading] = useState(false)
  const [isYearFilterError, setIsYearFilterError] = useState(false)

  // The summary API accepts comma-separated item IDs.
  const itemIdParam = useMemo(() => {
    if (!itemsFilter || itemsFilter.length === 0) return undefined
    return itemsFilter.map((item) => item.value).join(',')
  }, [itemsFilter])
  const selectedItemIds = useMemo(() => new Set(itemsFilter.map((item) => String(item.value))), [itemsFilter])

  // Compute date range based on active tab
  const selectedMonth = moment(selectedMonthYear)?.format('YYYY-MM')

  const dateRange = useMemo(() => {
    if (activeTab === 'tahunan') {
      const year = selectedYearAnnual?.value || new Date().getFullYear()
      return [`${year}-01-01`, `${year}-12-31`]
    } else {
      const month = moment(selectedMonth, 'YYYY-MM')
      return [month.clone().startOf('month').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        month.clone().endOf('month').format('YYYY-MM-DDTHH:mm:ss.SSS')]
    }
  }, [activeTab, selectedYearAnnual, selectedMonth])

  const { currentData: monthlyProductsData, isFetching: isMonthlyProductsLoading, isError: isMonthlyProductsError } =
    useFetchSummaryProductsQuery({
      startDate: dateRange[0],
      endDate: dateRange[1],
      counterpartId: counterpart,
      page: 0,
      size: 10000,
    }, { skip: activeTab !== 'bulanan' })

  const { currentData: yearlyProductsData } = useFetchSummaryProductsQuery({
    startDate: moment(dateRange[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
    endDate: moment(dateRange[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
    counterpartId: counterpart,
    page: 0,
    size: 10000,
  }, { skip: activeTab !== 'tahunan' })

  const activeProductRows = useMemo(() => activeTab === 'tahunan'
    ? yearlyProductsData?.items || []
    : monthlyProductsData?.items || [],
  [activeTab, yearlyProductsData, monthlyProductsData])

  const monthlyRows = useMemo(() => {
    const rows = (monthlyProductsData?.items || []).filter(hasTransactionValue)
    return selectedItemIds.size ? filterProductRows(rows, selectedItemIds) : rows
  }, [monthlyProductsData, selectedItemIds])

  const monthlySummary = useMemo(() => summarizeProductRows(monthlyRows), [monthlyRows])

  useEffect(() => {
    if (activeTab !== 'tahunan' || !selectedItemIds.size) return
    let cancelled = false
    setYearMonthRows(null)
    setIsYearFilterLoading(true)
    setIsYearFilterError(false)
    const year = selectedYearAnnual?.value || new Date().getFullYear()
    Promise.all(Array.from({ length: 12 }, async (_, index) => {
      const month = moment(`${year}-${index + 1}`, 'YYYY-M')
      const result = await fetchYearMonthProducts({
        startDate: month.clone().startOf('month').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        endDate: month.clone().endOf('month').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        counterpartId: counterpart,
        page: 0,
        size: 10000,
      }).unwrap()
      return result?.items || []
    })).then((rows) => {
      if (!cancelled) setYearMonthRows(rows)
    }).catch(() => {
      if (!cancelled) {
        setYearMonthRows(null)
        setIsYearFilterError(true)
      }
    }).finally(() => {
      if (!cancelled) setIsYearFilterLoading(false)
    })
    return () => { cancelled = true }
  }, [activeTab, selectedYearAnnual, counterpart, itemIdParam, selectedItemIds.size, fetchYearMonthProducts])

  const filteredYearSummary = useMemo(() => {
    if (!yearMonthRows) return null
    return buildFilteredYearSummary(yearMonthRows, selectedItemIds)
  }, [yearMonthRows, selectedItemIds])

  const currentSummaryData = activeTab === 'tahunan'
    ? (selectedItemIds.size ? filteredYearSummary : summaryYearData)
    : monthlySummary
  const isCurrentSummaryLoading = activeTab === 'tahunan'
    ? (selectedItemIds.size ? isYearFilterLoading : isSummaryYearLoading)
    : isMonthlyProductsLoading
  const hasSummaryError = activeTab === 'tahunan'
    ? (selectedItemIds.size ? isYearFilterError : isSummaryYearError)
    : isMonthlyProductsError

  // Generate year options
  useEffect(() => {
    const currentYear = new Date().getFullYear()
    const createdAtUser = profile?.createdAt
      ? new Date(profile.createdAt).getFullYear()
      : currentYear

    const startYear = Math.min(createdAtUser, currentYear - 4)
    const years = []
    for (let i = currentYear; i >= startYear; i--) {
      years.push({ value: i, label: String(i) })
    }

    setYearOptionsAnnual(years)

    if (!selectedYearAnnual) {
      const currentYearOption =
        years.find((option) => option.value === currentYear) || years[0]
      setSelectedYearAnnual(currentYearOption)
    }
  }, [profile, selectedYearAnnual])

  // Fetch summary + counterparts when filters change
  useEffect(() => {
    if (dateRange?.[0] && dateRange?.[1]) {
      if (activeTab === 'tahunan') {
        const year = selectedYearAnnual?.value || new Date().getFullYear()
        fetchSummaryYear({
          year,
          counterpartId: counterpart,
          itemId: itemIdParam,
        })
      }

      fetchCounterparts({
        startDate: dateRange[0],
        endDate: dateRange[1],
        size: 200,
      })
    }
  }, [
    activeTab,
    selectedYearAnnual,
    dateRange,
    counterpart,
    itemIdParam,
    fetchSummaryYear,
    fetchCounterparts,
  ])

  // Build counterpart options from API
  const counterpartOptions = useMemo(() => {
    if (!counterpartsData?.items) return []
    return counterpartsData.items.map((c) => ({
      value: c.id,
      label: c.name,
      sides: c.sides,
    }))
  }, [counterpartsData])

  // Offer only products with purchase or sales value in the selected period.
  const itemOptions = useMemo(() => {
    const options = new Map()
    const images = new Map((itemsData?.items || []).map((item) => [String(item.id), item.linkFoto]))
    activeProductRows.filter(hasTransactionValue).forEach((row) => {
      options.set(String(row.id), {
        label: row.name || '-',
        value: row.id,
        image: images.get(String(row.id)) || row.linkFoto || row.imageUrl || row.image,
      })
    })
    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [activeProductRows, itemsData])

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (counterpart) count++
    if (itemsFilter.length > 0) count += itemsFilter.length
    return count
  }, [counterpart, itemsFilter])

  const handleResetFilter = () => {
    setCounterpart(undefined)
    setItemsFilter([])
  }

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey)
  }

  const handleYearChangeAnnual = (selectedOption) => {
    setSelectedYearAnnual(selectedOption)
  }

  const handleChangeMonthYear = (selectedDates) => {
    if (selectedDates.length > 0) {
      const date = selectedDates[0]
      setSelectedMonthYear(date)
    }
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Ringkasan Transaksi'} />
      </div>

      <div className="d-flex flex-column gap-2 mb-2">
        <Card style={{ borderRadius: '12px', border: '1px solid #EAECF0' }}>
          <CardBody>
            {/* Tab Switcher */}
            <div className="mb-2 d-flex flex-column gap-3">
              <PMBTabSwitcher
                activeTab={activeTab}
                tabs={[
                  { key: 'tahunan', label: 'Tahunan' },
                  { key: 'bulanan', label: 'Bulanan' },
                ]}
                onTabChange={handleTabChange}
                testId="summary-tab"
              />
            </div>

            {/* Title + Action buttons */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5
                className="fw-bold mb-0"
                style={{ color: '#101828', fontSize: '1.1rem' }}
              >
                Ringkasan Transaksi
              </h5>
              <div className="d-flex gap-2 align-items-center">
                {activeFilterCount > 0 && (
                  <CustomButton
                    onClick={handleResetFilter}
                    color="danger"
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      minWidth: '40px',
                    }}
                    testId="summary-reset-filter-button"
                  >
                    <X size={16} color="white" />
                  </CustomButton>
                )}
                <CustomButton
                  onClick={() => setIsShowFilter((prev) => !prev)}
                  color="primary"
                  outline
                  className="position-relative"
                  testId="summary-filter-button"
                >
                  <div className="d-flex align-items-center gap-2">
                    <Filter size={15} />
                    <span>Filter</span>
                    {activeFilterCount > 0 && (
                      <Badge
                        color="danger"
                        pill
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          fontSize: '10px',
                        }}
                      >
                        {activeFilterCount}
                      </Badge>
                    )}
                    {isShowFilter ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>
                </CustomButton>
              </div>
            </div>

            {/* Filter panel */}
            <Collapse isOpen={isShowFilter}>
              <div
                className="p-3 mb-3"
                style={{
                  background: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #EAECF0',
                }}
              >
                <Row>
                  <Col xs={12} md={4} className="mb-3">
                    {activeTab === 'tahunan' ? (
                      <>
                        <P className="mb-2">Tahun</P>
                        <PMBSelect
                          value={selectedYearAnnual}
                          onChange={handleYearChangeAnnual}
                          options={yearOptionsAnnual}
                          isMulti={false}
                          placeholder="Pilih tahun"
                          testId="summary-year-select"
                        />
                      </>
                    ) : (
                      <>
                        <P className="mb-2">Bulan</P>
                        <div
                          className="d-flex align-items-center border rounded bg-white"
                          style={{ height: 42 }}
                        >
                          <Calendar size={17} className="ms-3" />
                          <Flatpickr
                            value={
                              selectedMonthYear
                                ? moment(selectedMonthYear).format('YYYY-MM')
                                : moment().format('YYYY-MM')
                            }
                            onChange={handleChangeMonthYear}
                            data-testid="summary-month-picker"
                            options={{
                              altInput: true,
                              altFormat: 'F Y',
                              dateFormat: 'Y-m',
                              defaultDate: selectedMonthYear
                                ? moment(selectedMonthYear).format('YYYY-MM')
                                : moment().format('YYYY-MM'),
                              disableMobile: true,
                              maxDate: moment().format('YYYY-MM'),
                              minDate: profile?.createdAt
                                ? moment(profile.createdAt).format('YYYY-MM-DD')
                                : undefined,
                              plugins: [
                                new MonthSelectPlugin({
                                  shorthand: false,
                                  dateFormat: 'Y-m',
                                  altFormat: 'F Y',
                                }),
                              ],
                            }}
                            className="form-control bg-transparent border-0 shadow-none"
                          />
                        </div>
                      </>
                    )}
                  </Col>

                  <Col xs={12} md={4} className="mb-3">
                    <p className="mb-2" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                      Penjual/Pembeli
                    </p>
                    <PMBSelect
                      style={{ width: '100%' }}
                      testId="summary-filter-counterpart"
                      options={[
                        { value: undefined, label: 'Semua' },
                        ...counterpartOptions,
                      ]}
                      value={
                        counterpartOptions.find(
                          (o) => String(o.value) === String(counterpart),
                        ) || { value: undefined, label: 'Semua' }
                      }
                      onChange={(e) =>
                        setCounterpart(e?.value ?? undefined)
                      }
                    />
                  </Col>

                  <Col xs={12} md={4} className="mb-3">
                    <PMBSelect
                      title="Barang"
                      style={{ width: '100%' }}
                      testId="summary-filter-commodity"
                      hideOverflowChips
                      value={itemsFilter}
                      onChange={(value) => {
                        setItemsFilter(value || [])
                      }}
                      options={itemOptions}
                      placeholder="Pilih barang..."
                      isClearable
                      isMulti
                      maxMenuHeight={300}
                      menuPosition="fixed"
                      menuPlacement="bottom"
                    />
                  </Col>
                </Row>

                <PMBSelectedSummary
                  label="Filter barang:"
                  value={itemsFilter}
                  options={itemOptions}
                  onChange={setItemsFilter}
                  testId="summary-filter-commodity-selected"
                />

              </div>
            </Collapse>

            {hasSummaryError ? (
              <Alert color="danger">Gagal memuat ringkasan transaksi. Coba pilih periode lagi.</Alert>
            ) : (
              <>
                <SummaryCards
                  summaryData={currentSummaryData}
                  isLoading={isCurrentSummaryLoading}
                />
                <ComparisonChart
                  summaryData={currentSummaryData}
                  isLoading={isCurrentSummaryLoading}
                />
                <SummaryTable
                  activeTab={activeTab}
                  summaryData={currentSummaryData}
                  monthlyRows={monthlyRows}
                  isLoading={isCurrentSummaryLoading}
                  dateRange={dateRange}
                  counterpartId={counterpart}
                  itemId={itemIdParam}
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default TransactionSummary
